import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Full compliance report (sample) — scrollable in-page preview
 * with its own dark/light theme toggle (independent of page theme).
 * Mirrors the design shipped in landing-static_20260420/veripass-landing-*.html.
 */
const SampleReport = () => {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const [drTheme, setDrTheme] = useState('dark');

  const t = isKo
    ? {
        heading: '전체 컴플라이언스 리포트 (샘플)',
        sub: '실제 VeriPass 리포트가 이렇게 표시됩니다. 아래 미리보기에서 다크/라이트 테마를 전환해 보세요.',
        toggleAria: '리포트 미리보기 테마 전환',
        reportLabel: '리포트',
        score: '컴플라이언스 점수',
        r1Title: 'UV Defense Complex SPF50',
        highRisk: '고위험 — 즉시 수정 필요',
        highRiskDesc: '이 문구는 FTC 입증 기준과 충돌할 수 있습니다.',
        analysis: '원문 문구 분석',
        flag1: '콜라겐 생성을 촉진합니다',
        flag2: '완전히 제거합니다',
        origText: ' 깊은 주름을 하룻밤 사이에.',
        answers: '입력한 답변',
        qClinical: '임상 근거 자료 보유 여부?',
        aNo: '아니오',
        prescription: '처방',
        original: '원문',
        saferWording: '더 안전한 표현',
        action: '적용',
        orig1: '촉진한다고',
        safe1: '도움을 줍니다',
        orig2: '완전히 제거합니다',
        safe2: '완화에 도움을 줍니다',
        apply: '적용',
        recommendedClaims: '권장 클레임',
        recClaimText: '지시에 따라 사용할 경우 잔주름 개선에 도움이 될 수 있습니다.',
        applyToMarketing: '마케팅 문구에 적용',
        drugClaim: '의약품 클레임',
        drugClaimDesc: '구조·기능 표현은 OTC/의약품 분류로 전환될 수 있습니다.',
        mocra: 'MoCRA',
        mocraDesc: '화장품에도 라벨링 및 중대 이상사례 보고 규정이 적용됩니다.',
        tip: '팁: 미국 출시 전, 촉진·제거 동사를 외관 변화 중심 표현으로 바꾸세요.',
        analyzed: '분석 근거',
        analyzedRow1: '컴플라이언스 체크 — 전체 문구',
        analyzedRow2: 'FDA 화장품 규정 대조',
        analyzedRow3: '과학적 근거',
        r2Title: '퍼밍 나이트 크림',
        safe: '안전 — 미국 출시 가능',
        safeDesc: '외관 변화만 언급하여 FDA 화장품 범주에 부합합니다.',
        r2Text: '매일 밤 4주 사용 시 탄력감 개선과 잔주름 완화에 도움을 줄 수 있습니다.',
        cosmetic: '화장품',
        cosmeticDesc: '클레임이 외관·미용 범주에 머뭅니다.',
        ftc: 'FTC',
        ftcDesc: '4주 패널 데이터로 기간 한정 클레임을 입증할 수 있습니다.',
        mocraDesc2: '책임자 및 제조시설 등록은 계속 필요합니다.',
        scrollHint: '↓ 프레임 내부를 스크롤하여 전체 샘플 리포트를 확인하세요',
      }
    : {
        heading: 'Full compliance report (sample)',
        sub: 'Here\u2019s how a real VeriPass report looks. Toggle dark/light inside the preview.',
        toggleAria: 'Toggle preview theme',
        reportLabel: 'Report',
        score: 'Compliance score',
        r1Title: 'UV Defense Complex SPF50',
        highRisk: 'HIGH RISK — Immediate revision needed',
        highRiskDesc: 'This copy may conflict with FTC substantiation expectations.',
        analysis: 'Original Copy Analysis',
        flag1: 'Stimulates',
        flag2: 'eliminate',
        origText: ' collagen production to ',
        origTextTail: ' deep wrinkles overnight.',
        answers: 'Your answers',
        qClinical: 'Clinical evidence on file?',
        aNo: 'No',
        prescription: 'Prescription',
        original: 'Original',
        saferWording: 'Safer wording',
        action: 'Action',
        orig1: 'stimulates',
        safe1: 'supports',
        orig2: 'eliminate',
        safe2: 'reduces the look of',
        apply: 'Apply',
        recommendedClaims: 'Recommended claims',
        recClaimText: 'Helps improve the appearance of fine lines when used as directed.',
        applyToMarketing: 'Apply to marketing copy',
        drugClaim: 'Drug claim',
        drugClaimDesc: 'Structure/function language may trigger OTC/drug pathways.',
        mocra: 'MoCRA',
        mocraDesc: 'Labeling and serious adverse event rules still apply to cosmetics.',
        tip: 'Tip: swap stimulation/removal verbs for appearance-only language before U.S. launch.',
        analyzed: 'How we analyzed this',
        analyzedRow1: 'Compliance check — full text',
        analyzedRow2: 'FDA vs. cosmetics regulations',
        analyzedRow3: 'Scientific evidence',
        r2Title: 'Firming Night Cream',
        safe: 'SAFE — Ready for U.S. launch',
        safeDesc: 'Appearance-only language aligns with FDA cosmetic scope.',
        r2Text: 'Helps improve the look of firmness and smooths the appearance of fine lines after 4 weeks of nightly use.',
        cosmetic: 'Cosmetic',
        cosmeticDesc: 'Claim stays in appearance/beautification territory.',
        ftc: 'FTC',
        ftcDesc: 'Time-bound claim is supported by internal 4-week panel data.',
        mocraDesc2: 'Responsible person + facility registration still required.',
        scrollHint: '↓ Scroll inside the frame to see the full sample report',
      };

  const toggleDr = () => setDrTheme((v) => (v === 'dark' ? 'light' : 'dark'));

  return (
    <section id="report-deep-dive" className="sample-report-section">
      <div className="wrap">
        <div className="section-head" style={{ marginBottom: 28 }}>
          <h2>{t.heading}</h2>
          <p style={{ marginTop: 8 }}>{t.sub}</p>
        </div>

        <div className={`sr-frame sr-${drTheme}`} data-vp-dr={drTheme}>
          {/* Browser chrome */}
          <div className="sr-chrome">
            <div className="sr-dots" aria-hidden="true">
              <span className="sr-dot" style={{ background: '#FF5F57' }} />
              <span className="sr-dot" style={{ background: '#FEBC2E' }} />
              <span className="sr-dot" style={{ background: '#28C840' }} />
            </div>
            <div className="sr-url">veripass.kr/reports/VP-20260414-0023</div>
            <button
              type="button"
              className="sr-theme-toggle"
              aria-label={t.toggleAria}
              aria-pressed={drTheme === 'light'}
              onClick={toggleDr}
            >
              {drTheme === 'light' ? (
                // sun
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2" /><path d="M12 20v2" />
                  <path d="m4.93 4.93 1.41 1.41" /><path d="m17.66 17.66 1.41 1.41" />
                  <path d="M2 12h2" /><path d="M20 12h2" />
                  <path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" />
                </svg>
              ) : (
                // moon
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
          </div>

          {/* Scrollable report body */}
          <div className="sr-body" role="region" aria-label={t.heading} tabIndex={0}>
            <div className="sr-inner">
              {/* Report #1 — HIGH RISK */}
              <article className="sr-card">
                <header className="sr-card-head">
                  <div>
                    <p className="sr-eyebrow">{t.reportLabel} · VP-20260414-0023</p>
                    <h3 className="sr-title">{t.r1Title}</h3>
                  </div>
                  <div className="sr-score">
                    <p className="sr-score-num">53<span className="sr-score-denom"> / 100</span></p>
                    <p className="sr-score-label">{t.score}</p>
                  </div>
                </header>
                <div className="sr-card-body">
                  <div className="sr-banner sr-banner-risk">
                    <p className="sr-banner-title">{t.highRisk}</p>
                    <p className="sr-banner-desc">{t.highRiskDesc}</p>
                  </div>

                  <div>
                    <p className="sr-block-label">{t.analysis}</p>
                    <div className="sr-quote">
                      {isKo ? (
                        <>
                          <span className="sr-flag">{t.flag1}</span>{' '}
                          <span className="sr-flag">{t.flag2}</span>{t.origText}
                        </>
                      ) : (
                        <>
                          <span className="sr-flag">{t.flag1}</span>{t.origText}
                          <span className="sr-flag">{t.flag2}</span>{t.origTextTail}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="sr-answers">
                    <p className="sr-answers-label">{t.answers}</p>
                    <p className="sr-answers-q">
                      {t.qClinical} <span className="sr-answers-a">{t.aNo}</span>
                    </p>
                  </div>

                  <div>
                    <p className="sr-block-label sr-block-strong">{t.prescription}</p>
                    <div className="sr-table">
                      <div className="sr-table-head">
                        <span>{t.original}</span>
                        <span>{t.saferWording}</span>
                        <span className="sr-th-action">{t.action}</span>
                      </div>
                      <div className="sr-table-row">
                        <span className="sr-strike">{t.orig1}</span>
                        <span className="sr-safe-text">{t.safe1}</span>
                        <button type="button" className="sr-btn sr-btn-apply" tabIndex={-1}>{t.apply}</button>
                      </div>
                      <div className="sr-table-row sr-table-row-last">
                        <span className="sr-strike">{t.orig2}</span>
                        <span className="sr-safe-text">{t.safe2}</span>
                        <button type="button" className="sr-btn sr-btn-apply" tabIndex={-1}>{t.apply}</button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="sr-block-label">{t.recommendedClaims}</p>
                    <div className="sr-reco">
                      <p className="sr-reco-text">{t.recClaimText}</p>
                      <button type="button" className="sr-btn sr-btn-marketing" tabIndex={-1}>{t.applyToMarketing}</button>
                    </div>
                  </div>

                  <div className="sr-grid sr-grid-2">
                    <div className="sr-mini">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sr-mini-ic sr-mini-ic-danger" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" x2="12" y1="8" y2="12" />
                        <line x1="12" x2="12.01" y1="16" y2="16" />
                      </svg>
                      <p className="sr-mini-label sr-mini-label-danger">{t.drugClaim}</p>
                      <p className="sr-mini-desc">{t.drugClaimDesc}</p>
                    </div>
                    <div className="sr-mini">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sr-mini-ic sr-mini-ic-caution" aria-hidden="true">
                        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                      </svg>
                      <p className="sr-mini-label sr-mini-label-caution">{t.mocra}</p>
                      <p className="sr-mini-desc">{t.mocraDesc}</p>
                    </div>
                  </div>

                  <div className="sr-tip">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                      <path d="M9 18h6" />
                      <path d="M10 22h4" />
                    </svg>
                    <p>{t.tip}</p>
                  </div>

                  <div className="sr-analyzed">
                    <p className="sr-analyzed-label">{t.analyzed}</p>
                    <div className="sr-analyzed-row"><span>{t.analyzedRow1}</span><span className="sr-chev">▶</span></div>
                    <div className="sr-analyzed-row"><span>{t.analyzedRow2}</span><span className="sr-chev">▶</span></div>
                    <div className="sr-analyzed-row"><span>{t.analyzedRow3}</span><span className="sr-chev">▶</span></div>
                  </div>
                </div>
              </article>

              {/* Report #2 — SAFE */}
              <article className="sr-card">
                <header className="sr-card-head">
                  <div>
                    <p className="sr-eyebrow">{t.reportLabel} · VP-20260414-0024</p>
                    <h3 className="sr-title">{t.r2Title}</h3>
                  </div>
                  <div className="sr-score">
                    <p className="sr-score-num">92<span className="sr-score-denom"> / 100</span></p>
                    <p className="sr-score-label">{t.score}</p>
                  </div>
                </header>
                <div className="sr-card-body">
                  <div className="sr-banner sr-banner-safe">
                    <p className="sr-banner-title">{t.safe}</p>
                    <p className="sr-banner-desc">{t.safeDesc}</p>
                  </div>
                  <div>
                    <p className="sr-block-label">{t.analysis}</p>
                    <div className="sr-quote">{t.r2Text}</div>
                  </div>
                  <div className="sr-grid sr-grid-3">
                    <div className="sr-mini">
                      <p className="sr-mini-label sr-mini-label-safe">{t.cosmetic}</p>
                      <p className="sr-mini-desc">{t.cosmeticDesc}</p>
                    </div>
                    <div className="sr-mini">
                      <p className="sr-mini-label sr-mini-label-safe">{t.ftc}</p>
                      <p className="sr-mini-desc">{t.ftcDesc}</p>
                    </div>
                    <div className="sr-mini">
                      <p className="sr-mini-label sr-mini-label-caution">{t.mocra}</p>
                      <p className="sr-mini-desc">{t.mocraDesc2}</p>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>

        <p className="sr-scroll-hint">{t.scrollHint}</p>
      </div>
    </section>
  );
};

export default SampleReport;
